import React, { useMemo, useState } from 'react'
import { FiPackage } from 'react-icons/fi'
import { ErrorNotification, SuccessNotification } from '../../utils'

export const COMMODITY_OPTIONS = [
  { id: 'oil_palm', label: 'Oil Palm', value: 'oil_palm' },
  { id: 'cocoa', label: 'Cocoa', value: 'cocoa' },
  { id: 'others', label: 'Others', value: 'others' },
]

/**
 * Build trading_commodities payload from modal selections.
 * Known options use API values; "others" uses the custom typed name.
 */
export const buildTradingCommoditiesPayload = (selectedIds, otherText = '') => {
  const commodities = []
  if (selectedIds.includes('oil_palm')) commodities.push('oil_palm')
  if (selectedIds.includes('cocoa')) commodities.push('cocoa')

  if (selectedIds.includes('others')) {
    const custom = String(otherText || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_')
    if (custom) commodities.push(custom)
  }

  return [...new Set(commodities)]
}

const CommoditySelectionModal = ({ onSubmit }) => {
  const [selected, setSelected] = useState([])
  const [otherText, setOtherText] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const othersSelected = selected.includes('others')

  const canSubmit = useMemo(() => {
    if (selected.length === 0) return false
    if (othersSelected && !String(otherText || '').trim()) return false
    return true
  }, [selected, othersSelected, otherText])

  const toggleOption = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    )
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!canSubmit || submitting) return

    const payload = buildTradingCommoditiesPayload(selected, otherText)
    if (payload.length === 0) {
      ErrorNotification('Please select at least one commodity')
      return
    }

    setSubmitting(true)
    try {
      await onSubmit?.(payload)
      SuccessNotification('Trading commodities saved')
    } catch (error) {
      const message =
        error?.response?.data?.trading_commodities?.[0] ||
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to save trading commodities'
      ErrorNotification(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/95 flex items-center justify-center z-[100000] backdrop-blur-sm">
      <div className="bg-white dark:bg-dark rounded-xl shadow-2xl w-full max-w-[480px] mx-4 p-8 relative animate-in fade-in zoom-in duration-300">
        <div className="flex flex-col items-center text-center mb-6">
          <div className="w-16 h-16 bg-yellowcolor/10 rounded-full flex items-center justify-center mb-4 border-4 border-yellowcolor/5">
            <FiPackage className="h-8 w-8 text-yellowcolor" />
          </div>
          <h2 className="text-2xl font-bold mb-2 dark:text-white">
            What do you trade in?
          </h2>
          <p className="text-gray-600 text-sm dark:text-gray-400 leading-relaxed">
            Select the commodities you trade so we can personalize your dashboard.
            This is required to continue.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 text-left">
          <div className="space-y-3">
            {COMMODITY_OPTIONS.map((option) => {
              const isChecked = selected.includes(option.id)
              return (
                <label
                  key={option.id}
                  className={`flex items-center gap-3 w-full rounded-xl border px-4 py-3 cursor-pointer transition-colors ${
                    isChecked
                      ? 'border-yellowcolor bg-yellowcolor/10'
                      : 'border-gray-200 dark:border-border-dark hover:border-yellowcolor/60'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleOption(option.id)}
                    className="h-4 w-4 rounded border-gray-300 text-yellowcolor focus:ring-yellowcolor"
                  />
                  <span className="font-medium text-black dark:text-white">
                    {option.label}
                  </span>
                </label>
              )
            })}
          </div>

          {othersSelected && (
            <div>
              <label
                htmlFor="other-commodity"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Specify other commodity<span className="text-red-500">*</span>
              </label>
              <input
                id="other-commodity"
                type="text"
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
                placeholder="e.g. maize, cashew, rubber"
                maxLength={80}
                className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-xl py-3 px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent"
                autoFocus
              />
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit || submitting}
            className="w-full h-14 rounded-xl bg-yellowcolor hover:bg-yellowcolor/90 text-black font-bold text-lg shadow-lg shadow-yellowcolor/20 transition-all active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? 'Saving...' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  )
}

export default CommoditySelectionModal
